import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart2, Crosshair, Upload, Camera } from 'lucide-react';
import { Button } from '../components/ds/Button';

/**
 * LandingPage - Split-screen landing page with dual user journeys
 *
 * Top half: Analytics journey (blue theme) - "Review Your Session"
 * Bottom half: Fitter journey (green theme) - "Get Fitted"
 *
 * Each section provides clear CTAs for CSV upload or photo capture,
 * navigating to their respective pages.
 */
export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 top-16 flex flex-col">
      {/* Analytics Section - Top Half */}
      <section className="flex-1 relative overflow-hidden bg-gradient-to-br from-blue-900/20 to-blue-600/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(59,130,246,0.15),transparent_50%)]" />

        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
          <div className="mb-6 p-4 bg-blue-500/10 rounded-full border border-blue-500/20">
            <BarChart2 size={48} className="text-blue-400" />
          </div>

          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3 bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">
            Review Your Session
          </h1>

          <p className="text-lg text-text-secondary mb-8 max-w-2xl">
            Upload your launch monitor data to analyze your shots, visualize trajectories, and identify patterns in your swing.
          </p>

          <div className="flex gap-4 flex-wrap justify-center">
            <Button
              variant="primary"
              size="lg"
              icon={Upload}
              onClick={() => navigate('/analytics')}
              className="min-w-[180px]"
            >
              Upload CSV
            </Button>
            <Button
              variant="secondary"
              size="lg"
              icon={Camera}
              onClick={() => navigate('/analytics')}
              className="min-w-[180px]"
            >
              Take Photo
            </Button>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Fitter Section - Bottom Half */}
      <section className="flex-1 relative overflow-hidden bg-gradient-to-br from-emerald-900/20 to-emerald-600/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(16,185,129,0.15),transparent_50%)]" />

        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
          <div className="mb-6 p-4 bg-emerald-500/10 rounded-full border border-emerald-500/20">
            <Crosshair size={48} className="text-emerald-400" />
          </div>

          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3 bg-gradient-to-r from-emerald-400 to-emerald-300 bg-clip-text text-transparent">
            Get Fitted
          </h1>

          <p className="text-lg text-text-secondary mb-8 max-w-2xl">
            Get personalized equipment recommendations based on your swing data. Find the perfect clubs for your game.
          </p>

          <div className="flex gap-4 flex-wrap justify-center">
            <Button
              variant="primary"
              size="lg"
              icon={Upload}
              onClick={() => navigate('/fitter')}
              className="min-w-[180px]"
            >
              Upload CSV
            </Button>
            <Button
              variant="secondary"
              size="lg"
              icon={Camera}
              onClick={() => navigate('/fitter')}
              className="min-w-[180px]"
            >
              Take Photo
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
